import React from 'react';

import { createFilterOptions } from '@mui/material';
import { useStates } from '@uplift/queries';
import { State } from '@uplift/types';

import { Autocomplete, AutocompleteMulti } from '../AutocompleteInput';
import { BaseTextFieldProps } from '../BaseTextField';

const stateFilterOptions = createFilterOptions<State>({
  stringify: (opt) => `${opt.fullName ?? ''} ${opt.shortName ?? ''}`,
});

export type AutoCompleteProps = {
  id?: string;
  name: string;
  label?: string;
  className?: string;
  fullWidth?: boolean;
  inputClasses?: BaseTextFieldProps['classes'];
  availableStatesOnly?: boolean;
  testId?: string;
  multiple?: boolean;
  autoComplete?: string;
  disableCloseOnSelect?: boolean;
  excludeStates?: string[];
} & Partial<Pick<React.ComponentProps<typeof Autocomplete>, 'onChange' | 'color' | 'autoFocus'>>;

export const StatesAutocomplete: React.VFC<AutoCompleteProps> = ({ id, name, availableStatesOnly = false, testId = 'states-autocomplete', multiple = false, disableCloseOnSelect = true, excludeStates, autoComplete, ...props }) => {
  const { states } = useStates();
  const filteredStates = React.useMemo(() => {
    let result = states;

    if (availableStatesOnly) {
      result = result.filter(state => state?.available);
    }

    if (excludeStates) {
      result = result.filter(state => !excludeStates.includes(state.id));
    }

    return result;
  }, [ states, availableStatesOnly, excludeStates ]);

  const autoFiller = (value: string) => {
    const state = states.find(o => o.fullName === value || o.shortName === value) ?? null;

    if (!state) {
      return null;
    }

    return { value: state, inputValue: state.fullName ?? '' };
  };

  if (multiple) {
    return (
      <AutocompleteMulti
        {...props}
        size="small"
        color={props.color as any}
        name={name}
        options={filteredStates}
        getOptionLabel={(opt: State) => opt?.fullName ?? ''}
        filterOptions={stateFilterOptions}
        extractId
        data-testid={testId}
        disableCloseOnSelect={disableCloseOnSelect}
        autoComplete={autoComplete}
      />
    );
  }

  return (
    <Autocomplete
      {...props}
      id={id ?? name}
      name={name}
      options={filteredStates}
      getOptionLabel={(opt: State) => opt.fullName ?? ''}
      transformValue={v => v?.id}
      autoFiller={autoFiller}
      blurOnSelect
      getOptionKey={opt => opt.id}
      selectOnFocus={false}
      data-testid={testId}
    />
  );
};
